import React, { useMemo } from 'react';
import {
  Layers,
  Package,
  Settings,
  Shield,
  AlertTriangle,
  TrendingUp,
  Info,
  ChevronRight
} from 'lucide-react';
import { SCORING_WEIGHTS, LEVEL_THRESHOLDS } from '../../config/cisaEvidenceModel';
import './ScoreBreakdownPanel.css';

/**
 * ScoreBreakdownPanel - Expandable panel showing overall scoring methodology
 *
 * Shows:
 * - Overall derived score with confidence indicator
 * - Three-layer breakdown (average across all functions)
 * - Level distribution
 * - Functions with override warnings
 */
function ScoreBreakdownPanel({
  derivedScores = {},
  overallScore,
  pillarScores = {},
  isExpanded,
  onToggle
}) {
  // Calculate aggregate metrics
  const aggregateMetrics = useMemo(() => {
    const scores = Object.values(derivedScores);
    if (scores.length === 0) {
      return {
        avgCapability: 0,
        avgConfig: 0,
        avgEvidence: 0,
        avgWeighted: 0,
        overrideCount: 0,
        expiredCount: 0
      };
    }

    let totalCapability = 0;
    let totalConfig = 0;
    let totalEvidence = 0;
    let totalWeighted = 0;
    let overrideCount = 0;
    let expiredCount = 0;

    for (const score of scores) {
      totalCapability += score.capabilityCoverage;
      totalConfig += score.configurationCompleteness;
      totalEvidence += score.evidenceConfidence;
      totalWeighted += score.weightedScore;
      if (score.hasOverride) overrideCount++;
      expiredCount += score.expiredEvidenceCount || 0;
    }

    return {
      avgCapability: Math.round(totalCapability / scores.length),
      avgConfig: Math.round(totalConfig / scores.length),
      avgEvidence: Math.round(totalEvidence / scores.length),
      avgWeighted: Math.round(totalWeighted / scores.length),
      overrideCount,
      expiredCount,
      totalFunctions: scores.length
    };
  }, [derivedScores]);

  // Calculate level distribution from derived scores
  const levelDistribution = useMemo(() => {
    const dist = { TRADITIONAL: 0, INITIAL: 0, ADVANCED: 0, OPTIMAL: 0 };
    for (const score of Object.values(derivedScores)) {
      const level = score.derivedLevel || 'TRADITIONAL';
      dist[level]++;
    }
    return dist;
  }, [derivedScores]);

  const getConfidenceLevel = (avgWeighted) => {
    if (avgWeighted >= 85) return { label: 'High Confidence', color: '#10b981' };
    if (avgWeighted >= 65) return { label: 'Medium Confidence', color: '#3b82f6' };
    if (avgWeighted >= 40) return { label: 'Low Confidence', color: '#f59e0b' };
    return { label: 'Very Low Confidence', color: '#ef4444' };
  };

  const confidence = getConfidenceLevel(aggregateMetrics.avgWeighted);
  const totalFunctions = Object.keys(derivedScores).length;

  return (
    <div className={`sbp-panel ${isExpanded ? 'sbp-expanded' : ''}`}>
      {/* Collapsed Header */}
      <button className="sbp-header" onClick={onToggle}>
        <div className="sbp-header-left">
          <Layers size={18} />
          <span className="sbp-header-title">Three-Layer Scoring</span>
          <span
            className="sbp-confidence-badge"
            style={{ background: `${confidence.color}20`, color: confidence.color }}
          >
            {confidence.label}
          </span>
        </div>
        <div className="sbp-header-right">
          <div className="sbp-mini-scores">
            <div className="sbp-mini-score" title="Capability Coverage">
              <Package size={12} />
              <span>{aggregateMetrics.avgCapability}%</span>
            </div>
            <div className="sbp-mini-score" title="Config Completeness">
              <Settings size={12} />
              <span>{aggregateMetrics.avgConfig}%</span>
            </div>
            <div className="sbp-mini-score" title="Evidence Confidence">
              <Shield size={12} />
              <span>{aggregateMetrics.avgEvidence}%</span>
            </div>
          </div>
          <ChevronRight
            size={16}
            className={`sbp-chevron ${isExpanded ? 'sbp-chevron-expanded' : ''}`}
          />
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="sbp-content">
          {/* Methodology Explanation */}
          <div className="sbp-methodology">
            <div className="sbp-method-item">
              <div className="sbp-method-icon" style={{ background: '#6366f120', color: '#6366f1' }}>
                <Package size={16} />
              </div>
              <div className="sbp-method-info">
                <span className="sbp-method-name">Capability Coverage</span>
                <span className="sbp-method-desc">Products adopted for each function</span>
              </div>
              <span className="sbp-method-weight">{Math.round(SCORING_WEIGHTS.capabilityCoverage * 100)}%</span>
              <span className="sbp-method-score">{aggregateMetrics.avgCapability}%</span>
            </div>

            <div className="sbp-method-item">
              <div className="sbp-method-icon" style={{ background: '#8b5cf620', color: '#8b5cf6' }}>
                <Settings size={16} />
              </div>
              <div className="sbp-method-info">
                <span className="sbp-method-name">Configuration Completeness</span>
                <span className="sbp-method-desc">Config steps completed per level</span>
              </div>
              <span className="sbp-method-weight">{Math.round(SCORING_WEIGHTS.configurationCompleteness * 100)}%</span>
              <span className="sbp-method-score">{aggregateMetrics.avgConfig}%</span>
            </div>

            <div className="sbp-method-item">
              <div className="sbp-method-icon" style={{ background: '#10b98120', color: '#10b981' }}>
                <Shield size={16} />
              </div>
              <div className="sbp-method-info">
                <span className="sbp-method-name">Evidence Confidence</span>
                <span className="sbp-method-desc">Verification checks completed</span>
              </div>
              <span className="sbp-method-weight">{Math.round(SCORING_WEIGHTS.evidenceConfidence * 100)}%</span>
              <span className="sbp-method-score">{aggregateMetrics.avgEvidence}%</span>
            </div>
          </div>

          {/* Level Distribution (Derived) */}
          <div className="sbp-distribution">
            <h4>Derived Level Distribution</h4>
            <div className="sbp-dist-bars">
              {['OPTIMAL', 'ADVANCED', 'INITIAL', 'TRADITIONAL'].map(level => {
                const count = levelDistribution[level];
                const percentage = totalFunctions > 0 ? (count / totalFunctions) * 100 : 0;
                const colors = {
                  TRADITIONAL: '#6b7280',
                  INITIAL: '#eab308',
                  ADVANCED: '#3b82f6',
                  OPTIMAL: '#10b981'
                };

                return (
                  <div key={level} className="sbp-dist-row">
                    <span className="sbp-dist-level" style={{ color: colors[level] }}>
                      {level.charAt(0) + level.slice(1).toLowerCase()}
                    </span>
                    <div className="sbp-dist-bar">
                      <div
                        className="sbp-dist-fill"
                        style={{ width: `${percentage}%`, background: colors[level] }}
                      />
                    </div>
                    <span className="sbp-dist-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Warnings */}
          {(aggregateMetrics.overrideCount > 0 || aggregateMetrics.expiredCount > 0) && (
            <div className="sbp-warnings">
              {aggregateMetrics.overrideCount > 0 && (
                <div className="sbp-warning sbp-warning-override">
                  <AlertTriangle size={14} />
                  <span>
                    <strong>{aggregateMetrics.overrideCount}</strong> function(s) have manual
                    overrides that differ from derived levels
                  </span>
                </div>
              )}
              {aggregateMetrics.expiredCount > 0 && (
                <div className="sbp-warning sbp-warning-expired">
                  <AlertTriangle size={14} />
                  <span>
                    <strong>{aggregateMetrics.expiredCount}</strong> evidence item(s) have expired
                    and may be affecting confidence scores
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Thresholds Info */}
          <div className="sbp-thresholds">
            <Info size={14} />
            <span>
              Thresholds: Optimal ≥{LEVEL_THRESHOLDS.OPTIMAL}% | Advanced ≥{LEVEL_THRESHOLDS.ADVANCED}% |
              Initial ≥{LEVEL_THRESHOLDS.INITIAL}% | Traditional &lt;{LEVEL_THRESHOLDS.INITIAL}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScoreBreakdownPanel;
